import React, { useCallback } from "react";
import styled, { css } from "styled-components";
import { useCombobox } from "downshift";
import moment from "moment-timezone";
import _ from "lodash";
import { toast } from "react-toastify";

import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import Grid from "src/library/components/Hedron";
import { Text } from "src/library/components/Text";
import { Input } from "src/library/components/Forms";
import usePromotionalTag from "src/modules/promotions/hooks/usePromotionalTag";
import InputColor from "src/library/components/Forms/InputColor";
import { Button } from "src/library/components/Button";
import { ProductSearchComponent } from "src/core/products/components/ProductSearchComponent";
import { useSearchApi } from "src/core/shared/api/search.api";
import {
  IProduct,
  ISearchRequest
} from "src/core/products/api/products.api";
import {
  CreateEditProductPromotionalTag,
  CreateEditPromotionalTag
} from "src/modules/promotions/api/promotional-tags.api";
import DraggableDatedProducts from "src/core/products/components/DraggableDatedProducts";
import { DraggableDatedProduct } from "src/core/products/components/DraggableDatedProductsRow";
import generateListProductsParams from "src/core/products/utils/generate-list-products-params";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

interface UrlParams {
  promotionalTagId: string
}

/**
 * Page used by admins to create or edit promotional tags
 */
const CreateEditPromotionalTagPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = ({ title, ...props }) => {
  const { match, history } = props;
  const promotionalTagId = match ? parseInt(match.params.promotionalTagId, 10) : undefined;
  const [ listSearchResponse, getSearch ] = useSearchApi("SEARCH_PRODUCTS");
  const draggableDatedProductsListRef = React.useRef<{currentDatedProducts: DraggableDatedProduct[]}>(null);
  const checkPermission = useCheckUserPermission();
  const canAmendPromo = checkPermission("amend-promotion");

  const {
    isLoading, promotionalTag, setCurrentPromotionalTag, createPromotionalTagReq, updatePromotionalTagReq
  } = usePromotionalTag(history, promotionalTagId);

  // handle selecting a product
  const productDownShiftProps = useCombobox<IProduct>({
    items: listSearchResponse.data ? listSearchResponse.data.result : [],
    itemToString: () => "",
    onSelectedItemChange: changes => {
      if (changes.selectedItem && promotionalTag) {
        let updatedProductPromotionalTags = promotionalTag?.productPromotionalTags;

        if (promotionalTag?.productPromotionalTags) {
          // avoid duplicate products in list
          const productAlreadyExists = _.find(promotionalTag.productPromotionalTags, [ "productId", changes.selectedItem.id ]);

          if (productAlreadyExists) {
            toast.error(`The product ${changes.selectedItem.partNumber} already associated with this promotional tag.`);

            return;
          }
        }

        const newProductPromotionalTag: CreateEditProductPromotionalTag = {
          productId: changes.selectedItem.id,
          startDate: moment.utc().toDate(),
          product: changes.selectedItem
        };

        // if editing promotional tag, attach promotionalTagId to new item
        if (promotionalTag.id) {
          newProductPromotionalTag.promotionalTagId = promotionalTag.id;
        }

        if (updatedProductPromotionalTags) {
          updatedProductPromotionalTags = [ newProductPromotionalTag, ...updatedProductPromotionalTags ];
        } else {
          updatedProductPromotionalTags = [ newProductPromotionalTag ];
        }

        setCurrentPromotionalTag({
          ...promotionalTag,
          productPromotionalTags: updatedProductPromotionalTags
        });

        return;
      }
    }
  });

  // fetch the most recent state of productPromotionalTags list on save
  const handleSave = useCallback((promotionalTag: CreateEditPromotionalTag) => {
    const promotionalTagToSave = { ...promotionalTag };
    const currentProductPromotionalTags = draggableDatedProductsListRef?.current?.currentDatedProducts;

    // if product promotional tags exist, ensure reformatted date ahead of request
    if (currentProductPromotionalTags) {
      promotionalTagToSave.productPromotionalTags = currentProductPromotionalTags.map(tag => ({
        ...tag,
        startDate: moment.utc(tag.startDate).format("YYYY-MM-DD"),
        endDate: tag.endDate ? moment.utc(tag.endDate).format("YYYY-MM-DD") : undefined
      }));
    }

    // if the id exists, it means we're updating
    if (promotionalTagToSave.id) {
      updatePromotionalTagReq({
        data: promotionalTagToSave,
        promotionalTagId: promotionalTagToSave.id
      });
    } else {
      createPromotionalTagReq(promotionalTagToSave);
    }
  }, [ createPromotionalTagReq, updatePromotionalTagReq ]);

  return (
    <Layout
      title={title}
      loading={isLoading && !promotionalTag}
      {...props}
    >
      <MainContentWrapper fill>
        <StyledCreateEditWrapper>
          {promotionalTag && (
            <>
              <Text as="h4">
                Promotional Tag Label:
              </Text>

              <Input
                as="input"
                name="label"
                autoHideLabels
                label=""
                value={promotionalTag.label}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setCurrentPromotionalTag({
                    ...promotionalTag,
                    label: event.target.value
                  });
                }}
              />

              <div className="field">
                <Text as="h4">
                  Tag Color:
                </Text>

                <InputColor
                  text={promotionalTag.label}
                  hexValue={promotionalTag.color}
                  handleChange={color => setCurrentPromotionalTag({
                    ...promotionalTag,
                    color
                  })}
                />
              </div>

              <div className="field">
                <Text as="h4">
                  Product:
                </Text>

                <Text>
                  Search to add products to this tag.
                </Text>

                <div className="product-search">
                  <ProductSearchComponent
                    error={listSearchResponse.error}
                    onSearch={(data: ISearchRequest) => getSearch(generateListProductsParams(data))}
                    loading={listSearchResponse.pending}
                    searchResults={listSearchResponse.data ? listSearchResponse.data.result : []}
                    downshiftProps={productDownShiftProps}
                    placeholder={`Search ${props.terminology.product.plural.text}...`}
                    idsToDisable={_.compact(_.map(promotionalTag.productPromotionalTags || [], "productId"))}
                  />
                </div>
              </div>

              <div className="field">
                <DraggableDatedProducts
                  datedProducts={promotionalTag.productPromotionalTags as DraggableDatedProduct[] || []}
                  lockSortableFeature
                  ref={draggableDatedProductsListRef}
                />
              </div>

              <div className="field">
                <Button
                  $buttonType="primary"
                  onClick={() => {
                    if (!canAmendPromo) {
                      toast.error("You do not have permission to edit promotional tags. Please speak to your administrator to request permission.");
                    } else {
                      handleSave(promotionalTag);
                    }
                  }}
                  loading={isLoading}
                >
                  Save
                </Button>
              </div>
            </>

          )}

        </StyledCreateEditWrapper>
      </MainContentWrapper>
    </Layout>
  );
};

const StyledCreateEditWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    flex-direction: column;

    padding: ${theme.spacing.padding.xs};

    .field {
      padding-top: ${theme.spacing.padding.lg};

      .product-search {
        margin: ${theme.spacing.padding.lg} 0;
      }
    }
  `}
`;

export default CreateEditPromotionalTagPage;
